import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  Button,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import configServ from "../services/config";
import toast from "react-hot-toast";

const LeaveCard = ({
  leaveTypes = [],
  leaveList = [],
  setIschange,
  isEdit,
  setIsEdit,
  editData,
  setIsCreate,
  setEditData,
  ischange,
}) => {
  const { company_id, userId } = useSelector(state => state.globalVariables);

  const [form, setForm] = useState({
    leave_type_id: "",
    from_date: "",
    to_date: "",
    reason: "",
    leave_list_id: "",
  });

  const [errors, setErrors] = useState({});

  // Handle Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "leave_list_id") {
      const selectedHoliday = leaveList.find((i) => i.id === value);
      const holidayDate = selectedHoliday?.holiday_date
        ? selectedHoliday.holiday_date.split("T")[0]
        : "";

      setForm((prev) => ({
        ...prev,
        leave_list_id: value,
        to_date: holidayDate,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectedLeave = leaveTypes.find(
    (item) => item.id === form.leave_type_id
  );
  const isRH = selectedLeave?.leave_code === "RH";

  // Validation
  const validate = () => {
    let newErrors = {};
    if (!form.leave_type_id) newErrors.leave_type_id = "Leave type is required";
    if (isRH) {
      if (!form.leave_list_id) newErrors.leave_list_id = "Holiday is required";
      if (!form.to_date) newErrors.to_date = "To date is required";
    } else {
      if (!form.from_date) newErrors.from_date = "Start date is required";
      if (!form.to_date) newErrors.to_date = "End date is required";
    }
    if (!form.reason) newErrors.reason = "Reason is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = { company_id, employee_id: userId, ...form };
    try {
      let res = isEdit ? await configServ.updateLeave(editData.id, payload) : await configServ.createLeave(payload);
      if (res?.status) {
        toast.success(isEdit ? "Updated successfully" : "Created successfully");
        handleReset();
        setIsCreate(false);
        setIschange(!ischange);
      } else {
        toast.error("Something went wrong");
      }
    } catch (err) {
      toast.error("Server error");
    }
  };

  const handleReset = () => {
    setForm({ leave_type_id: "", from_date: "", to_date: "", reason: "", leave_list_id: "" });
    setIsEdit(false);
    setEditData({});
  };

  useEffect(() => {
    if (editData && Object.keys(editData).length > 0) {
      setForm({
        leave_type_id: editData.leave_type_id || "",
        from_date: editData.from_date || "",
        to_date: editData.to_date || "",
        reason: editData.reason || "",
        leave_list_id: editData.leave_list_id || "",
      });
    }
  }, [editData]);

  // Modern Dark Theme Styles
  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(30, 41, 59, 0.5)",
      borderRadius: "12px",
      transition: "all 0.3s ease",
      color: "#f8fafc",
      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
      "&:hover fieldset": { borderColor: "#6366f1" },
      "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { color: "#94a3b8" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#818cf8" },
    "& .MuiSvgIcon-root": { color: "#94a3b8" },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "800px",
        background: "linear-gradient(145deg, #0f172a 0%, #1e293b 100%)",
        padding: { xs: 3, md: 4 },
        borderRadius: "24px",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, letterSpacing: "0.5px" }}>
          {isEdit ? "Edit Leave Request" : "New Leave Application"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          Please fill in the details below to submit your request.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" sx={inputStyle}>
            <InputLabel>Leave Type</InputLabel>
            <Select
              name="leave_type_id"
              value={form.leave_type_id}
              onChange={handleChange}
              label="Leave Type"
              MenuProps={{ PaperProps: { sx: { bgcolor: "#1e293b", color: "#fff" } } }}
            >
              {leaveTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.leave_name}</MenuItem>
              ))}
            </Select>
            {errors.leave_type_id && <Typography sx={{ color: "#f87171", fontSize: "0.75rem", mt: 0.5, ml: 1 }}>{errors.leave_type_id}</Typography>}
          </FormControl>
        </Grid>

        {isRH ? (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={inputStyle}>
              <InputLabel>Select Holiday</InputLabel>
              <Select name="leave_list_id" value={form.leave_list_id} onChange={handleChange} label="Select Holiday">
                {leaveList.map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
              </Select>
              {errors.leave_list_id && <Typography sx={{ color: "#f87171", fontSize: "0.75rem", mt: 0.5 }}>{errors.leave_list_id}</Typography>}
            </FormControl>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              label="From Date"
              name="from_date"
              value={form.from_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
            />
            {errors.from_date && <Typography sx={{ color: "#f87171", fontSize: "0.75rem", mt: 0.5 }}>{errors.from_date}</Typography>}
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            type="date"
            label="To Date"
            name="to_date"
            value={form.to_date}
            onChange={handleChange}
            fullWidth
            disabled={isRH}
            InputLabelProps={{ shrink: true }}
            sx={inputStyle}
          />
          {errors.to_date && <Typography sx={{ color: "#f87171", fontSize: "0.75rem", mt: 0.5 }}>{errors.to_date}</Typography>}
        </Grid>

        <Grid item xs={12}>
          <TextField
            multiline
            rows={3}
            placeholder="Reason for leave..."
            label="Reason"
            name="reason"
            value={form.reason}
            onChange={handleChange}
            fullWidth
            sx={inputStyle}
            InputLabelProps={{ shrink: true }}
          />
          {errors.reason && <Typography sx={{ color: "#f87171", fontSize: "0.75rem", mt: 0.5 }}>{errors.reason}</Typography>}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
        <Button
          onClick={() => setIsCreate(false)}
          sx={{ color: "#94a3b8", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            background: "linear-gradient(90deg, #4f46e5, #7c3aed)",
            borderRadius: "12px",
            padding: "10px 32px",
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.4)",
            "&:hover": {
              background: "linear-gradient(90deg, #4338ca, #6d28d9)",
            },
          }}
        >
          {isEdit ? "Update Application" : "Submit Request"}
        </Button>
      </Box>
    </Paper>
  );
};

export default LeaveCard;